/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/require-default-props */
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { SectionSlot } from '@youshift/shared/types';
import {
  ColorName,
  Spanning,
  getShadeForLabels,
  returnColor,
} from '@youshift/shared/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useChainContext } from '../../../layouts/ChainLayout';
import { ArrangedSectionSlot } from '../../../pages/Manager/IterationConfig/Sections/types';
import { LabelBadge } from '../../Calendars/LabelBadge';
import CounterAcronymBadge from '../../CounterAcronymBadge';
import { ShowRestPeriod } from '../ShowRestPeriod';
import { getArrangedSectionSlot } from './helpers';

interface SectionSlotConfigProps {
  slot:
    | (SectionSlot & {
        spanning: Spanning;
      } & {
        start: Date;
        end: Date;
      })
    | ArrangedSectionSlot;
  week?: Date[];
  sectionColor: ColorName;
  onIconClick: (slot: ArrangedSectionSlot) => void;
  onSlotClick: (slotId: number) => void;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  isSelected?: boolean;
}

export function SectionSlotConfig({
  slot,
  sectionColor,
  week,
  onIconClick,
  onSlotClick,
  Icon,
  isSelected,
}: SectionSlotConfigProps) {
  const { t } = useTranslation();
  const { counters, slotLabels } = useChainContext();

  const arrangedSectionSlot = getArrangedSectionSlot(slot, week);
  const shade = getShadeForLabels(slot.slot_labels);
  const color = returnColor(sectionColor, shade);

  return (
    <div
      key={`${arrangedSectionSlot.start_time}-${arrangedSectionSlot.day_idx}-${slot.slot_labels[0]}`}
      className="rounded-md flex flex-col justify-between relative gap-0.5 h-min"
      style={{
        backgroundColor: color,
        border: '1px solid gray',
        gridColumnStart: arrangedSectionSlot.start_position,
        gridColumnEnd: arrangedSectionSlot.end_position,
      }}
    >
      {/* Main content container to avoid overlap */}
      <div className="flex flex-row">
        <button
          className="ml-0.5 text-gray-500"
          onClick={() => onIconClick && onIconClick(arrangedSectionSlot)}
          aria-label={t('generic.delete')}
        >
          <Icon className="md:h-4 md:w-4 w-2 h-2" />
        </button>
        <button
          className="w-full"
          onClick={() => {
            if (onSlotClick) {
              onSlotClick(slot.id_section_slot!);
            }
          }}
        />
      </div>
      <button
        onClick={() => {
          if (onSlotClick) {
            onSlotClick(slot.id_section_slot!);
          }
        }}
      >
        <CheckCircleIconSolid
          className={`absolute -top-3 right-1 w-5 h-5 text-purple-600 bg-purple-100 rounded-full ${isSelected ? '' : 'hidden'}`}
        />
        <div className="flex flex-col items-center">
          <p
            className={`text-center md:text-xs text-xxxs ${
              arrangedSectionSlot.duration_minutes < 480
                ? 'px-0.5 pb-1'
                : 'px-2 pb-1'
            }`}
          >
            {`${slot.spanning === 'end' ? '' : arrangedSectionSlot.start_time} - ${arrangedSectionSlot.end_time}`}
          </p>
          {/* {children} */}
        </div>
      </button>
      <button
        onClick={() => {
          if (onSlotClick) {
            onSlotClick(slot.id_section_slot!);
          }
        }}
      >
        <ShowRestPeriod restPeriod={slot.rest_period} />
        <div className="flex flex-row gap-1 justify-start w-full flex-wrap mb-1 ml-1">
          {slot.slot_labels.map(labelId => (
            <LabelBadge
              labelColor={slotLabels[labelId]?.color}
              icon={slotLabels[labelId]?.icon}
            />
          ))}
        </div>
        {slot.custom_counter_increments && (
          <div className="flex flex-col gap-1 justify-start items-start w-full mb-1 ml-1 text-xs">
            {Object.entries(slot.custom_counter_increments).map(
              ([idCounter, inc]) => (
                <CounterAcronymBadge
                  key={idCounter}
                  acronym={counters[Number(idCounter)]?.acronym}
                  value={inc}
                />
              ),
            )}
          </div>
        )}
      </button>
    </div>
  );
}
